// Buyers.jsx
import React, { useEffect, useState } from "react";
import Navbar from "../Components/NavBar";
import "../css/LandingPage.css";
import "../css/NavBar.css";
import "../css/GuidePages.css";
import buyersGuidePdf from "../documents/Buying_Guide.pdf";

// pick a cover image that matches your PDF vibe
const COVER_IMG = "/imgs/guides/buyers-cover.jpg";

export default function Buyers() {
  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const [guideOpen, setGuideOpen] = useState(false);
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setGuideOpen(false);
    if (guideOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guideOpen]);

  const handlePrintGuide = () => {
    const w = window.open(buyersGuidePdf, "_blank", "noopener,noreferrer");
    setTimeout(() => {
      try { w?.focus(); w?.print(); } catch {}
    }, 600);
  };

  const houseHuntingTips = [
    { title: "Planning", text: "Write down must-haves, nice-to-haves, and what doesn’t matter." },
    { title: "Stay in Budget", text: "Get pre-approved before touring so you know which homes to focus on." },
    { title: "Location", text: "Consider proximity to work, neighborhood charm, and how the home sits on the lot." },
    { title: "Size & Floor Plan", text: "Think about how the space will be used now and in the future." },
    { title: "Neighborhood", text: "Drive by at different times—weekdays/weekends, day/evening—to gauge vibe." },
    { title: "Keep an Open Mind", text: "Look past cosmetic blemishes—focus on the home’s “bones” and potential." },
  ];

  const tenSteps = [
    "Finding an Agent",
    "Prepare Your Finances",
    "Get Pre-Approved",
    "Start Home Shopping",
    "Making an Offer",
    "Order an Inspection",
    "Negotiate Final Offer",
    "Appraisal Ordered",
    "Schedule Your Move",
    "Closing Day!",
  ];

  const buyerQuestions = [
    "What location are you looking in?",
    "How many bedrooms and bathrooms?",
    "Is school district important?",
    "What home square footage are you looking for?",
    "Do you need a walkout basement?",
    "Kitchen “must haves”?",
    "Gas or electric heating?",
    "Flat backyard or wooded backyard?",
    "Parking and/or garage spaces?",
    "Do you have a property you need to sell first before you purchase?",
  ];

  return (
    <div className="guide-page">
      <Navbar variant="buyers" />

      {/* PDF-like cover hero */}
      <section className="guide-hero" id="overview">
        <div className="guide-hero-media">
          <img src={COVER_IMG} alt="Buyers guide cover" />
        </div>

        <div className="guide-hero-panel">
          <p className="guide-hero-kicker">Kentucky Home Living</p>
          <h1 className="guide-hero-title">Buyer’s Guide</h1>
          <p className="guide-hero-subtitle">
            A guide to help you with your Kentucky home buying process.
          </p>

          <div className="guide-hero-actions">
            <button className="btn accent" onClick={() => setGuideOpen(true)}>View Guide</button>
            <a className="btn-secondary-guides" href="/contact">Talk to Brooke</a>
          </div>
        </div>
      </section>

      <main className="interior-inner">
        {/* Tips */}
        <section className="guide-section" id="tips">
          <div className="guide-section-header">
            <p className="guide-section-eyebrow">House Hunting Tips</p>
            <h2 className="guide-section-title">Happy house hunting</h2>
            <p className="guide-section-lede">
              Quick, practical reminders that keep your search focused and stress-free.
            </p>
          </div>

          <div className="guide-grid">
            {houseHuntingTips.map((t) => (
              <div className="guide-card" key={t.title}>
                <h4>{t.title}</h4>
                <p>{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 10 Steps */}
        <section className="guide-section" id="steps">
          <div className="guide-section-header">
            <p className="guide-section-eyebrow">Steps</p>
            <h2 className="guide-section-title">10 steps to buying a home</h2>
          </div>

          <div className="guide-grid">
            {tenSteps.map((label, idx) => (
              <div className="guide-card" key={label}>
                <div className="row">
                  <div className="badge-num">{idx + 1}</div>
                  <div>
                    <h4 style={{ margin: 0 }}>{label}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Questions */}
        <section className="guide-section" id="questions">
          <div className="guide-section-header">
            <p className="guide-section-eyebrow">For Buyers</p>
            <h2 className="guide-section-title">Top 10 questions to start your search</h2>
          </div>

          <div className="guide-grid">
            {buyerQuestions.map((q, idx) => (
              <div className="guide-card" key={q}>
                <div className="row">
                  <div className="badge-num">{idx + 1}</div>
                  <p style={{ margin: 0 }}>{q}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* modal stays same */}
      {guideOpen && (
        <div
          className="guide-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Buyers Guide"
          onClick={() => setGuideOpen(false)}
        >
          <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="guide-modal-header">
              <div>
                <div className="guide-modal-title">Buyer Guide</div>
                <div className="guide-modal-subtitle">
                  {isMobile ? "Open guide in a new tab." : "Browse here, or print from a new tab."}
                </div>
              </div>

              {!isMobile && (
                <div className="guide-modal-actions">
                  <button className="btn accent small" onClick={handlePrintGuide}>Print</button>
                  <button className="btn secondary small" onClick={() => setGuideOpen(false)}>Close</button>
                </div>
              )}
            </div>

            <div className="guide-modal-body">
              {isMobile ? (
                <div className="guide-modal-mobile">
                  <a className="btn accent" href={buyersGuidePdf} target="_blank" rel="noreferrer">
                    Open Guide
                  </a>
                  <button className="btn secondary" onClick={() => setGuideOpen(false)}>Close</button>
                </div>
              ) : (
                <iframe title="Buyer Guide PDF" src={buyersGuidePdf} className="guide-modal-frame" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
